import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { InputSuffix } from "#components/atoms/InputSuffix/InputSuffix";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Field } from "#components/molecules/Field/Field";
import {
  NestingInputsFormInput,
  NestingInputsFormValues,
} from "#modules/layoutsV2/domain/nestingInputsFormSchema";
import { DimensionsSelect } from "#modules/layoutsV2/view/DimensionsSelect";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import {
  FormatterMaterialParameter,
  MaterialGradeType,
  MaterialLayout,
  PartTypeType,
} from "shared";

type Props = {
  materialLayout: MaterialLayout | null;
  partType: PartTypeType | null;
  partSubType: string | null;
  materialGrade: MaterialGradeType;
  materialName: string;
  quoteRequestLineId?: string;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
  control: Control<NestingInputsFormInput, unknown, NestingInputsFormValues>;
  isCalculating: boolean;
  isNestingInProgress: boolean;
  isDirty: boolean;
  onCalculate: () => void;
};

export const NestingInputs = ({
  materialLayout,
  partType,
  partSubType,
  materialGrade,
  materialName,
  quoteRequestLineId,
  partDimensionsWithOverthickness,
  control,
  isCalculating,
  isNestingInProgress,
  isDirty,
  onCalculate,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" align="flex-start" gap={spacing[4]} style={{ alignSelf: "stretch" }}>
      <Flex align="flex-start" gap={spacing[6]} style={{ alignSelf: "stretch" }}>
        <Flex flex="0 0 276px" align="flex-start" gap={spacing[6]}>
          {materialLayout ? (
            <Controller
              name="material"
              control={control}
              render={({ field }) => (
                <DimensionsSelect
                  selectedMaterialId={field.value}
                  onSelectMaterial={field.onChange}
                  materialLayout={materialLayout}
                  materialGrade={materialGrade}
                  materialName={materialName}
                  quoteRequestLineId={quoteRequestLineId}
                  partType={partType}
                  partSubType={partSubType}
                  partDimensionsWithOverthickness={partDimensionsWithOverthickness}
                />
              )}
            />
          ) : null}
        </Flex>
        <Flex direction="column" align="flex-start" gap={spacing[1]} flex="1 0 0" miw={0}>
          <Field label={t("layout.sidePanel.cutWidth")}>
            <Controller
              name="cutWidth"
              control={control}
              render={({ field, fieldState }) => (
                <InputSuffix
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  suffix="mm"
                  error={Boolean(fieldState.error)}
                  aria-label={t("layout.sidePanel.cutWidth")}
                />
              )}
            />
          </Field>
        </Flex>
        <Flex direction="column" align="flex-start" gap={spacing[1]} flex="1 0 0" miw={0}>
          <Field
            label={t("layout.sidePanel.skeleton")}
            labelInfo={
              <Tooltip type="large" label={t("layout.sidePanel.skeletonTooltip")} position="bottom">
                <Icon name="circleInfo" size={12} color={colors.icon.secondary} />
              </Tooltip>
            }
          >
            <Controller
              name="skeletonWidth"
              control={control}
              render={({ field, fieldState }) => (
                <InputSuffix
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  suffix="mm"
                  error={Boolean(fieldState.error)}
                  aria-label={t("layout.sidePanel.skeleton")}
                />
              )}
            />
          </Field>
        </Flex>
      </Flex>
      <Button
        variant="secondary"
        loading={isCalculating}
        disabled={!isDirty || isNestingInProgress}
        onClick={onCalculate}
      >
        {t("layout.computeNesting")}
      </Button>
    </Flex>
  );
};
